import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Activity } from 'types/interfaces/activity';
import { ACTIVITY_TYPE_CONFIG } from '../constants';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import { Points } from 'components/Points/Points';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { formatRelativeTime, formatAccessibleTime } from 'utils/relativeTime';
import ActivityUserBadgeFlyout from './ActivityUserBadgeFlyout';
import './ActivityFeedListItem.css';

interface ActivityFeedListItemProps {
  activity: Activity;
  isNew?: boolean;
  onAnimationEnd?: (id: string) => void;
  /** True while the row is animating out before being dropped from the feed. */
  isExiting?: boolean;
}

const ActivityFeedListItem: React.FC<ActivityFeedListItemProps> = ({
  activity,
  isNew = false,
  onAnimationEnd,
  isExiting = false,
}) => {
  const navigate = useNavigate();
  const config = ACTIVITY_TYPE_CONFIG[activity.type];
  const [isHighlighted, setIsHighlighted] = useState(isNew);
  const [badgeFlyout, setBadgeFlyout] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!isNew) return;
    const timer = setTimeout(() => {
      setIsHighlighted(false);
      onAnimationEnd?.(activity.id);
    }, 1800);
    return () => clearTimeout(timer);
  }, [isNew, activity.id, onAnimationEnd]);

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      const currentSrc = img.src;
      const imageComingSoon = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';
      if (!img.dataset.triedLarge && currentSrc.includes('/normal/')) {
        img.dataset.triedLarge = 'true';
        img.src = currentSrc.replace('/normal/', '/large/');
        return;
      }
      if (!img.dataset.triedSmall && (currentSrc.includes('/large/') || currentSrc.includes('/normal/'))) {
        img.dataset.triedSmall = 'true';
        img.src = currentSrc.replace('/large/', '/small/').replace('/normal/', '/small/');
        return;
      }
      if (img.src !== imageComingSoon) {
        img.src = imageComingSoon;
      }
    },
    []
  );

  return (
    <div
      className={`list-item${isNew ? ' list-item--entering' : ''}${isHighlighted ? ' list-item--highlight' : ''}${isExiting ? ' list-item--exiting' : ''}`}
      style={{ '--glow-color': config ? `${config.color}44` : undefined } as React.CSSProperties}
    >
      {/* Thumbnail */}
      <div
        className="list-item__thumbnail"
        onClick={() => navigate(buildCardUrl(activity.card))}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') navigate(buildCardUrl(activity.card));
        }}
      >
        <img
          src={activity.card.imageUrl}
          alt={activity.card.name}
          loading="lazy"
          onError={handleImageError}
          className="list-item__thumbnail-img"
        />
      </div>

      {/* Card name + set */}
      <div className="list-item__card-info">
        <Link
          to={buildCardUrl(activity.card)}
          className="list-item__card-name"
          onClick={(e) => e.stopPropagation()}
        >
          {activity.card.name}
        </Link>
        <span className="list-item__set-code">
          {activity.card.setCode?.toUpperCase()}
          {activity.card.collectorNumber && (
            <span className="list-item__collector-number"> #{activity.card.collectorNumber}</span>
          )}
        </span>
      </div>

      {/* User info */}
      <Link
        to={`/profile/u/${encodeURIComponent(activity.user.username)}`}
        className="list-item__user-link"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setBadgeFlyout({ x: rect.right + 8, y: rect.top });
        }}
        onMouseLeave={() => setBadgeFlyout(null)}
      >
        <AvatarWithFlag
          avatarUrl={activity.user.avatarUrl}
          countryCode={activity.user.countryCode}
          username={activity.user.username}
          size={24}
          isElite={activity.user?.isElite}
          showAnimatedBorder={activity.user?.showAnimatedBorder}
          borderColor1={activity.user?.borderColor1}
          borderColor2={activity.user?.borderColor2}
        />
        <span className="list-item__username">{activity.user.username}</span>
        {activity.user?.isElite && <EliteBadge isElite={true} size={12} />}
      </Link>

      {/* Points */}
      <div className="list-item__points">
        {activity.pointValue != null ? (
          <Points points={activity.pointValue} type="small" />
        ) : (
          <span className="list-item__no-price">???</span>
        )}
      </div>

      {/* Timestamp */}
      <time
        className="list-item__timestamp"
        dateTime={activity.createdAt}
        title={formatAccessibleTime(activity.createdAt)}
      >
        {formatRelativeTime(activity.createdAt)}
      </time>

      {/* Type badge removed: redundant under single-select filters (the active category
          is always known). The timestamp above is kept. */}

      {badgeFlyout && (
        <ActivityUserBadgeFlyout
          userId={activity.user.id}
          username={activity.user.username}
          position={badgeFlyout}
          onClose={() => setBadgeFlyout(null)}
        />
      )}
    </div>
  );
};

export default ActivityFeedListItem;
